'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState, useRef } from 'react';
import { usePathname } from 'next/navigation';
import styled, { keyframes } from 'styled-components';

const PreloaderWrapper = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99999;
  background: linear-gradient(135deg, #152637 0%, #1a3245 50%, #152637 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  will-change: transform, opacity;
  backface-visibility: hidden;

  /* Ensure it covers everything */
  width: 100vw;
  height: 100vh;
  min-height: 100vh;

  /* Prevent any scrolling or overflow */
  overflow: hidden;

  .text-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: clamp(0.5rem, 2vw, 1.5rem);
    padding: 0 1rem;
    max-width: 95vw;
  }

  .main-text {
    font-family: inherit;
    font-size: clamp(2.2rem, 8vw, 7rem);
    color: #ffffff;
    text-align: center;
    font-weight: bold;
    line-height: 0.8;
    overflow: hidden;
    
    &.weight-400{
      font-weight: 500;
    }
    
    .word {
      display: inline-block;
      margin-right: clamp(0.1em, 2vw, 0.3em);
      overflow: hidden;

      .char {
        display: inline-block;
        transform: translateY(100%);
        transition: transform 0.8s cubic-bezier(0.215, 0.61, 0.355, 1);
        will-change: transform;
      }
    }
  }

  .sub-text {
    font-family: inherit;
    font-size: clamp(0.8rem, 2.5vw, 1.1rem);
    font-weight: 300;
    color: #fff;
    letter-spacing: clamp(0.05em, 1vw, 0.1em);
    text-transform: uppercase;
    text-align: center;
    margin-top: clamp(1rem, 3vw, 2rem);
    overflow: hidden;
    max-width: 90vw;
    line-height: 1.3;

    .char {
      display: inline-block;
      transform: translateY(100%);
      transition: transform 0.6s cubic-bezier(0.215, 0.61, 0.355, 1);
      will-change: transform;
    }
  }

.progress-container {
    position: absolute;
    bottom: 3rem;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 1rem;
    font-family: 'Inter', sans-serif;
    font-size: clamp(0.75rem, 1.5vw, 1rem);
    color: #fff;
    letter-spacing: 0.05em;

    .progress-line {
      width: 60px;
      height: 1px;
      background: rgba(255, 255, 255, 0.3);
      position: relative;
      overflow: hidden;

      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        background: rgba(255, 255, 255, 1);
        width: 0%;
        transition: width 0.1s ease-out;
      }
    }
  }

  @media (max-width: 768px) {
    .main-text {
      font-size: clamp(2rem, 12vw, 4rem);
    }

    .progress-container {
      bottom: 2rem;
    }
  }
`;

const AnimatedText = ({ text, className, delay = 0 }) => {
  const words = text.split(' ');
  
  return (
    <div className={className}>
      {words.map((word, wordIndex) => (
        <span key={wordIndex} className="word">
          {word.split('').map((char, charIndex) => (
            <span
              key={charIndex}
              className="char"
              style={{
                transitionDelay: `${delay + (wordIndex * 50) + (charIndex * 30)}ms`
              }}
            >
              {char}
            </span>
          ))}
          {/* Add space after each word except the last one */}
          {wordIndex < words.length - 1 && (
            <span
              className="char"
              style={{
                transitionDelay: `${delay + (wordIndex * 50) + (word.length * 30)}ms`
              }}
            >
              &nbsp;
            </span>
          )}
        </span>
      ))}
    </div>
  );
};
const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;


const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    radial-gradient(circle at 25% 25%, rgba(221, 34, 63, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
    radial-gradient(circle at 50% 50%, rgba(221, 34, 63, 0.05) 0%, transparent 50%);
  animation: ${rotate} 30s linear infinite;
  z-index: 1;
`;


const Preloader = ({ onComplete }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  const [textAnimated, setTextAnimated] = useState(false);
  const pathname = usePathname();
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const progressLineRef = useRef(null);

  // Preloader variants for smooth exit
  const preloaderVariants = {
    initial: {
      opacity: 1,
      scale: 1,
    },
    exit: {
      opacity: 0,
      scale: 1.05,
      transition: {
        duration: 0.8,
        ease: [0.76, 0, 0.24, 1],
        when: "beforeChildren"
      },
    },
  };

  // Text exit variants
  const textExitVariants = {
    initial: { opacity: 1, y: 0 },
    exit: {
      opacity: 0,
      y: -30,
      transition: {
        duration: 0.6,
        ease: [0.76, 0, 0.24, 1],
      },
    },
  };

  // Animate text on mount immediately
  useEffect(() => {
    setTextAnimated(true);
    // Animate all characters immediately
    const chars = document.querySelectorAll('.char');
    chars.forEach((char) => {
      char.style.transform = 'translateY(0%)';
    });
  }, []);

  // Update progress line
  useEffect(() => {
    if (progressLineRef.current) {
      progressLineRef.current.style.width = `${progress}%`;
    }
  }, [progress]);

  useEffect(() => {
    // Only run once on mount, not on pathname change
    if (intervalRef.current || timeoutRef.current) {
      return; // Already initialized
    }

    // Simulate realistic loading progress with better pacing
    const startTime = Date.now();
    const duration = 700; // ~0.7 s — keeps the animation readable without blocking LCP

    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progressPercent = Math.min((elapsed / duration) * 100, 100);
      
      // Slower progress in the beginning to let users read the text
      let easedProgress;
      if (progressPercent < 30) {
        // Very slow start - let text animations complete
        easedProgress = progressPercent * 0.3;
      } else if (progressPercent < 70) {
        // Moderate speed in middle
        easedProgress = 9 + (progressPercent - 30) * 1.5;
      } else {
        // Normal speed towards the end
        easedProgress = 69 + (progressPercent - 70) * 1.0;
      }

      setProgress(Math.floor(Math.min(easedProgress, 100)));

      if (progressPercent >= 100) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        
        // Give users more time to see the completed text before exit
        timeoutRef.current = setTimeout(() => {
          setIsVisible(false);
          
          // Call onComplete after exit animation starts
          setTimeout(() => {
            if (onComplete) {
              onComplete();
            }
          }, 100); //Changed from 300
        }, 200); // Increased delay for better UX //Changed from 800
      }
    }, 16); // ~60fps

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [onComplete]); // Remove pathname dependency

  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <PreloaderWrapper
          key="preloader"
          variants={preloaderVariants}
          initial="initial"
          animate="initial"
          exit="exit"
        >
          <BackgroundPattern/>
          <motion.div 
            className="text-container"
            variants={textExitVariants}
            initial="initial"
            exit="exit"
          >
            <AnimatedText 
              text="YGEN" 
              className="main-text"
              delay={0}
            />
            <AnimatedText 
              text="Automation"
              className="main-text weight-400"
              delay={400}
            />
            <AnimatedText 
              text="Innovative engineering solutions"
              className="sub-text"
              delay={800}
            />
          </motion.div>

          <motion.div 
            className="progress-container"
            variants={textExitVariants}
            initial="initial"
            exit="exit"
          >
            <span aria-hidden="true">{progress.toString().padStart(2, '0')}-100</span>
            <div
              className="progress-line"
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Loading page">
              <div 
                ref={progressLineRef}
                style={{ 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '100%',
                  background: 'rgba(220, 0, 78, 1)',
                  width: '0%',
                  transition: 'width 0.1s ease-out'
                }}
              />
            </div>
            <span aria-hidden="true">Please wait a few seconds.</span>
          </motion.div>
        </PreloaderWrapper>
      )}
    </AnimatePresence>
  );
};

export default Preloader;
